import React, { useMemo, useState } from "react";
import { View, StyleSheet, ScrollView, TextInput } from "react-native";
import NtText from "../../components/NtText";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import { useTheme } from "../../context/ThemeContext";
import countries from "../data/countries";

/**
 * Inline searchable country dropdown. Click the field to open a panel
 * anchored below it; type to filter; pick a country to close.
 *
 * Click the chevron again (or pick a country) to close. No external
 * dependencies.
 */
function NtOnboardingCountryPicker({
  title = "Country *",
  value,
  onChange,
  placeholder = "Select a country",
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return countries;
    const q = query.trim().toLowerCase();
    return countries.filter((c) => c.toLowerCase().includes(q));
  }, [query]);

  const handleOpen = () => {
    setOpen(true);
    setQuery("");
  };
  const handleClose = () => {
    setOpen(false);
    setQuery("");
  };
  const handlePick = (country) => {
    onChange && onChange(country);
    handleClose();
  };

  return (
    <View style={styles.container}>
      <NtModalInputTitle style={styles.title}>{title}</NtModalInputTitle>

      <View style={styles.anchor}>
        <NtTouchableEffect
          style={[styles.field, open && styles.fieldOpen]}
          onPress={() => (open ? handleClose() : handleOpen())}
        >
          <NtText style={[styles.value, !value && styles.placeholder]}>
            {value || placeholder}
          </NtText>
          <NtMaterialIcon
            name={open ? "chevron-up" : "chevron-down"}
            color={theme.textDim}
            size={18}
          />
        </NtTouchableEffect>

        {open ? (
          <View style={styles.popover}>
            <View style={styles.searchRow}>
              <NtMaterialIcon name="magnify" size={16} color={theme.textDim} />
              <TextInput
                autoFocus
                value={query}
                onChangeText={setQuery}
                placeholder="Search countries"
                placeholderTextColor={theme.textMute}
                style={styles.searchInput}
              />
              {query ? (
                <NtTouchableEffect onPress={() => setQuery("")} style={styles.clearBtn}>
                  <NtMaterialIcon name="close" size={14} color={theme.textDim} />
                </NtTouchableEffect>
              ) : null}
            </View>

            <ScrollView
              style={styles.list}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={true}
            >
              {filtered.length === 0 ? (
                <View style={styles.emptyRow}>
                  <NtText style={styles.emptyText}>No matches</NtText>
                </View>
              ) : (
                filtered.map((country) => {
                  const selected = country === value;
                  return (
                    <NtTouchableEffect
                      key={country}
                      style={[styles.row, selected && styles.rowSelected]}
                      onPress={() => handlePick(country)}
                    >
                      <NtText
                        style={[styles.rowText, selected && styles.rowTextSelected]}
                      >
                        {country}
                      </NtText>
                      {selected ? (
                        <NtMaterialIcon name="check" size={14} color={theme.brand2} />
                      ) : null}
                    </NtTouchableEffect>
                  );
                })
              )}
            </ScrollView>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: { marginBottom: 8 },
    title: { marginLeft: 4, marginBottom: 4 },

    // The anchor wraps the field + popover so the popover positions relative
    // to the trigger via `position: absolute`.
    anchor: { position: "relative", zIndex: 100 },

    field: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      paddingHorizontal: 12,
      height: 38,
    },
    fieldOpen: { borderColor: t.brand2 },
    value: { flex: 1, fontSize: 13, color: t.text },
    placeholder: { color: t.textMute },

    popover: {
      position: "absolute",
      top: 42,
      left: 0,
      right: 0,
      backgroundColor: t.surface,
      borderWidth: 1,
      borderColor: t.border,
      borderRadius: t.radius,
      maxHeight: 280,
      overflow: "hidden",
      zIndex: 200,
      // Web-friendly shadow.
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.35,
      shadowRadius: 16,
      elevation: 8,
    },
    searchRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
      backgroundColor: t.surface2,
    },
    searchInput: {
      flex: 1,
      fontSize: 13,
      color: t.text,
      fontFamily: "System",
      paddingVertical: 4,
      outlineStyle: "none",
    },
    clearBtn: { padding: 4 },

    list: { maxHeight: 240 },
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
    },
    rowSelected: { backgroundColor: t.pillBg },
    rowText: { fontSize: 13, color: t.text },
    rowTextSelected: { color: t.brand2, fontWeight: "600" },

    emptyRow: { paddingHorizontal: 12, paddingVertical: 14 },
    emptyText: { fontSize: 12, color: t.textDim, fontStyle: "italic" },
  });

export default NtOnboardingCountryPicker;
